import { C } from '../../theme.js';
import { FieldLabel, TextInput } from '../../components/Primitives.jsx';

// ── Output Column Name(s) (Optional) ─────────────────────────────────────
// Shared by every scaling/encoding/outlier-handling block that lets a user
// pick MULTIPLE input columns and writes one output column per input
// column selected. With 1 column selected this renders exactly like the
// single TextInput these blocks used to have (and, if the step was saved
// before this field existed, prefills from the old `output_column` value
// via `legacyValue`). With 2+ columns selected it renders one row per
// selected column — its name on the left, its own output-name TextInput on
// the right — instead of one box whose value would otherwise apply to (and
// collide across) every selected column at once.
//
// Config shape: `output_columns` — a dict mapping an input column's
// name to its custom output name/prefix. Backend counterpart:
// `output_name_for(cfg, cols, col, key, default)` in
// server/app/engine/helpers.py, which prefers this map, then falls back to
// the legacy single `output_column` (only when exactly one column is
// configured — otherwise ambiguous), then the block's auto-generated name.
export function OutputColumnNamesField({
  columns, value, onChange, autoName, legacyValue,
  label = 'Output Column Name(s) (Optional)',
  hint = 'Optional — override the auto-generated output column name for each selected input column. Leave blank to use the default shown as the placeholder.',
}) {
  const cols = Array.isArray(columns) ? columns.filter(Boolean) : [];
  if (!cols.length) return null;
  const map = value && typeof value === 'object' ? value : {};

  const setOne = (col, name) => {
    const next = { ...map };
    if (name) next[col] = name; else delete next[col];
    onChange(next);
  };

  return (
    <div style={{ marginTop: 10 }}>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
        {cols.map((col, i) => {
          const current = map[col] ?? (cols.length === 1 && i === 0 ? (legacyValue || '') : '');
          return (
            <div key={col} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {cols.length > 1 && (
                <div
                  title={col}
                  style={{
                    flex: '0 0 42%', fontSize: '.78rem', color: C.fg3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </div>
              )}
              <div style={{ flex: 1 }}>
                <TextInput
                  value={current}
                  onChange={(v) => setOne(col, v)}
                  placeholder={`Auto (e.g., ${autoName ? autoName(col) : col})`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
